import type { DtcCodeEntity } from '@packages/domain/dtc-codes';

import { Typography } from '@packages/ui/shared';

import { useDtcCodeSearch } from '../../../../../../hooks/use-dtc-code-search';
import { DtcCodesTable } from './dtc-codes-table/index.js';
import { SearchDtcCode } from './search-dtc-code.js';

/**
 * Props for the {@link ManageDtcCodes} component.
 */
type ManageDtcCodesProps = {
    /** The current list of DTC codes on the case. */
    dtcCodeData: DtcCodeEntity[];

    /** Error message from the most recent failed DTC operation. */
    errorMessage: string;

    /** Per-code loading state keyed by DTC code string. */
    isDtcLoading: { [dtcCode: string]: boolean };

    /** Whether a primary code change is currently in flight. */
    isPrimaryUpdating: boolean;

    /** Callback to add a DTC code to the case. */
    onAddDtc: (code: DtcCodeEntity[]) => void;

    /** Callback to delete a DTC code from the case. */
    onDeleteDtc: (code: DtcCodeEntity) => void;

    /** Callback to change the primary DTC code. */
    onPrimaryChange: (code: DtcCodeEntity) => void;

    /** Callback to toggle freeze frame data for a DTC code. */
    onToggleFreezeFrame: (code: DtcCodeEntity) => void;
};

/**
 * Manages DTC codes for a draft Techline case. Renders a search typeahead for adding codes, an error
 * banner, and a table of existing codes with primary/freeze-frame/delete controls.
 *
 * @param props - the component props
 * @returns the DTC code management UI
 */
export function ManageDtcCodes({
    dtcCodeData,
    errorMessage,
    isDtcLoading,
    isPrimaryUpdating,
    onAddDtc,
    onDeleteDtc,
    onPrimaryChange,
    onToggleFreezeFrame,
}: ManageDtcCodesProps) {
    const { handleSearch, isSearching, searchResults } = useDtcCodeSearch({
        existingDtcCodes: dtcCodeData,
    });

    return (
        <div>
            <SearchDtcCode
                isLoading={isSearching}
                onChange={onAddDtc}
                onRemove={onDeleteDtc}
                onSearch={handleSearch}
                options={searchResults}
                selected={dtcCodeData}
            />

            {errorMessage && (
                <div className="mt-4">
                    <Typography color="redOne">{errorMessage}</Typography>
                </div>
            )}

            {dtcCodeData?.length > 0 && (
                <div className="mt-4">
                    <DtcCodesTable
                        dtcCodeData={dtcCodeData}
                        isDtcLoading={isDtcLoading}
                        isPrimaryUpdating={isPrimaryUpdating}
                        onDelete={onDeleteDtc}
                        onFreezeFrameChange={onToggleFreezeFrame}
                        onPrimaryChange={onPrimaryChange}
                    />
                </div>
            )}
        </div>
    );
}
